import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import RegistrationForm from '../components/RegistrationForm';
import PricingCard from '../components/PricingCard';
import ServiceCard from '../components/ServiceCard';
import TestimonialCard from '../components/TestimonialCard';
import CountUp from '../components/CountUp';
import RegistrationModal from '../components/RegistrationModal';
import Slider from 'react-slick';
import { pricingPackages, services, testimonials, faqs, statistics, processSteps } from '../mock';
import { Shield, Clock, Users, CheckCircle2, ArrowRight, Award, Lock, Zap } from 'lucide-react';

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const handleGetStarted = (pkg) => {
    setSelectedPackage(pkg || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section - Compact & Above the Fold */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 lg:py-12 overflow-hidden min-h-[85vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
            {/* Registration Form - Mobile First */}
            <div id="registration-form" className="flex justify-center lg:order-2">
              <RegistrationForm />
            </div>
            
            {/* Hero Content - Compact */}
            <div className="space-y-4 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-[#fd5a27]/20 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-[#fd5a27]/30">
                <Shield className="w-4 h-4 text-[#fd5a27]" />
                <span className="text-xs font-medium text-[#fd5a27]/80">USPTO-Approved Federal Protection</span>
              </div>
              
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight">
                Secure Your <span className="text-[#fd5a27]">Federal Trademark</span> Before Competitors Do
              </h1>
              
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed">
                Fast USPTO trademark filing with 98% approval rate. Protect your brand name, logo, and business identity across all 50 states. Fixed $45 pricing—no hidden fees.
              </p>

              {/* Trust Signals - Compact */}
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm text-gray-300">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>24-Hr Filing</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Money-Back</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>No Legal Degree</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-[#e04a1f] hover:to-[#d0421a] text-white px-6 py-3 text-sm sm:text-base transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  Get Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  className="js-chat border-[#fd5a27] text-[#fd5a27] hover:bg-[#fd5a27] hover:text-white px-6 py-3 text-sm sm:text-base transition-all duration-300 w-full sm:w-auto"
                >
                  Live Chat Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Social Proof */}
      <section className="bg-slate-900 py-12 lg:py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 text-center">
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg hover:bg-slate-800/50 transition-all">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] bg-clip-text text-transparent">
                <CountUp end={98} suffix="%" />
              </div>
              <div className="text-sm sm:text-base text-gray-300">USPTO Approval Rate</div>
              <div className="text-xs text-gray-500">vs. 50% industry average</div>
            </div>
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg hover:bg-slate-800/50 transition-all">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] bg-clip-text text-transparent">
                <CountUp end={10} suffix="k+" />
              </div>
              <div className="text-sm sm:text-base text-gray-300">Brands Protected</div>
              <div className="text-xs text-gray-500">Startups to Fortune 500</div>
            </div>
            <div className="space-y-2 bg-slate-800/30 p-6 rounded-lg hover:bg-slate-800/50 transition-all">
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] bg-clip-text text-transparent">
                24
              </div>
              <div className="text-sm sm:text-base text-gray-300">Hour Filing Guarantee</div>
              <div className="text-xs text-gray-500">Attorney review included</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - Why You Need This NOW */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-red-400 font-semibold mb-2 text-sm lg:text-base">⚠️ URGENT</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Your Brand Is At Risk Without Federal Trademark Protection
            </h2>
            <p className="text-sm sm:text-base text-gray-300 max-w-3xl mx-auto">
              Every day without trademark registration puts you at risk. Competitors can legally copy your brand, Amazon can suspend your account, and you lose legal ammunition against infringement.
            </p>
          </div>
          
          {/* Risk Factors - Mobile Slider, Desktop Grid */}
          <div className="md:hidden mt-12">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Lock className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Brand Theft Risk</h3>
                  <p className="text-sm text-gray-300">Without USPTO protection, competitors can legally use similar names and logos, confusing your customers and stealing market share.</p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Amazon Suspension</h3>
                  <p className="text-sm text-gray-300">Amazon Brand Registry requires federal trademark registration. Without it, you can't protect listings or stop counterfeiters on the platform.</p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
                  <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Lost Legal Rights</h3>
                  <p className="text-sm text-gray-300">Only federal registration gives you nationwide protection and legal standing to sue infringers. State filings offer minimal protection.</p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Brand Theft Risk</h3>
              <p className="text-sm text-gray-300">Without USPTO protection, competitors can legally use similar names and logos, confusing your customers and stealing market share.</p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Amazon Suspension</h3>
              <p className="text-sm text-gray-300">Amazon Brand Registry requires federal trademark registration. Without it, you can't protect listings or stop counterfeiters on the platform.</p>
            </div>
            
            <div className="bg-slate-900 p-6 rounded-xl shadow-sm border border-red-500/30 hover:border-red-500 transition-all duration-300">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Lost Legal Rights</h3>
              <p className="text-sm text-gray-300">Only federal registration gives you nationwide protection and legal standing to sue infringers. State filings offer minimal protection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">COMPREHENSIVE PROTECTION</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Complete USPTO Trademark Filing Service
            </h2>
            <p className="text-sm sm:text-base text-gray-300 max-w-2xl mx-auto">
              We handle every step of federal trademark registration—from comprehensive clearance searches to USPTO filing and office action responses. Attorney-backed protection at a fraction of law firm costs.
            </p>
          </div>
          
          {/* Services Grid */}
          <div className="md:hidden mt-12">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              <div className="px-2">
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Comprehensive Search</h3>
                  <p className="text-sm text-gray-300">Federal and state database clearance to prevent costly rejection</p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">USPTO Filing</h3>
                  <p className="text-sm text-gray-300">Expert application preparation with attorney review included</p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Office Action Response</h3>
                  <p className="text-sm text-gray-300">Professional handling of USPTO objections and examiner queries</p>
                </div>
              </div>
              
              <div className="px-2">
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
                  <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-[#fd5a27]" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Lifetime Monitoring</h3>
                  <p className="text-sm text-gray-300">Automated renewal reminders and trademark maintenance alerts</p>
                </div>
              </div>
            </Slider>
          </div>

          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Comprehensive Search</h3>
              <p className="text-sm text-gray-300">Federal and state database clearance to prevent costly rejection</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">USPTO Filing</h3>
              <p className="text-sm text-gray-300">Expert application preparation with attorney review included</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Office Action Response</h3>
              <p className="text-sm text-gray-300">Professional handling of USPTO objections and examiner queries</p>
            </div>
            
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-[#fd5a27] transition-all duration-300 hover:shadow-lg hover:shadow-[#fd5a27]/10">
              <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#fd5a27]" />
              </div>
              <h3 className="font-bold text-white mb-2">Lifetime Monitoring</h3>
              <p className="text-sm text-gray-300">Automated renewal reminders and trademark maintenance alerts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">SIMPLE 3-STEP PROCESS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Federal Trademark Registration in Minutes
            </h2>
          </div>
          
          <div className="sm:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {processSteps.map((step) => (
                <div key={step.step} className="px-2">
                  <div className="relative">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8 text-center hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27] transition-all duration-300">
                      <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-[#fd5a27] to-[#e04a1f] text-white rounded-full flex items-center justify-center text-xl lg:text-2xl font-bold mx-auto mb-4 shadow-lg shadow-[#fd5a27]/50">
                        {step.step}
                      </div>
                      <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{step.title}</h3>
                      <p className="text-sm lg:text-base text-gray-300">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden sm:grid sm:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 lg:p-8 text-center hover:shadow-2xl hover:shadow-[#fd5a27]/20 hover:border-[#fd5a27] transition-all duration-300">
                  <div className="w-14 h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-[#fd5a27] to-[#e04a1f] text-white rounded-full flex items-center justify-center text-xl lg:text-2xl font-bold mx-auto mb-4 shadow-lg shadow-[#fd5a27]/50">
                    {step.step}
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-sm lg:text-base text-gray-300">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Process Timeline */}
          <div className="mt-12 bg-[#fd5a27]/20 border border-[#fd5a27]/30 rounded-xl p-6 text-center">
            <p className="text-[#fd5a27]/80 font-semibold mb-2">⚡ FAST TRACK PROCESSING</p>
            <p className="text-white text-lg font-bold">Applications filed within 24 hours | USPTO review in 5-7 months</p>
            <p className="text-gray-400 text-sm mt-2">Protection begins immediately upon filing</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">COMPLETE IP PROTECTION</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Intellectual Property Services for Every Business
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              From trademark registration to copyright protection and business formation—comprehensive legal services tailored for startups, e-commerce brands, and content creators
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
            >
              {services.map((service) => (
                <div key={service.id} className="px-2">
                  <ServiceCard service={service} />
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:block">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={3}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                  }
                }
              ]}
            >
              {services.map((service) => (
                <div key={service.id} className="px-2 lg:px-4">
                  <ServiceCard service={service} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">TRANSPARENT PRICING</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Fixed-Price Trademark Registration Packages
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              No hidden fees. No surprise charges. Attorney review included in every package. Choose the protection level that fits your business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            {pricingPackages.map((pkg) => (
              <div key={pkg.id}>
                <PricingCard package={pkg} onGetStarted={handleGetStarted} />
              </div>
            ))}
          </div>

          {/* Pricing Note */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">* USPTO government filing fees additional ($250-$350 per class)</p>
            <p className="text-gray-400 text-sm mt-1">** All packages include attorney review and money-back guarantee</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-[#fd5a27]/90 via-[#e04a1f]/80 to-[#d0421a]/70 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 to-transparent"></div>
        <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="order-1 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?q=80&w=1200"
                alt="Professional Legal Team"
                className="rounded-xl shadow-2xl w-full"
              />
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                  <div className="text-2xl lg:text-3xl font-bold">10,000+</div>
                  <div className="text-xs lg:text-sm">Brands Protected</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                  <div className="text-2xl lg:text-3xl font-bold">98%</div>
                  <div className="text-xs lg:text-sm">Approval Rate</div>
                </div>
              </div>
            </div>
            
            <div className="order-2 lg:order-2">
              <p className="text-white/80 font-semibold mb-2 text-sm lg:text-base">WHY CHOOSE US</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 lg:mb-6">
                Attorney-Backed Protection at Startup Prices
              </h2>
              <p className="text-sm sm:text-base text-white/80 mb-6 lg:mb-8">
                We combine the expertise of a top-tier IP law firm with the efficiency of modern technology—giving you professional trademark protection without the $3,000+ legal fees.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-[#fd5a27] transition-all duration-300">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">Expert Legal Review</h3>
                  <p className="text-xs lg:text-sm text-white/80">Every application reviewed by licensed trademark attorneys—ensuring maximum approval chances</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-[#fd5a27] transition-all duration-300">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">24-Hour Filing</h3>
                  <p className="text-xs lg:text-sm text-white/80">Applications submitted to USPTO within 24 hours—protecting your priority filing date</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-[#fd5a27] transition-all duration-300">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">Money-Back Guarantee</h3>
                  <p className="text-xs lg:text-sm text-white/80">100% satisfaction guaranteed. If we can't file your trademark, you get a full refund—no questions asked</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-4 lg:p-6 rounded-xl border border-white/10 hover:bg-white/10 hover:border-[#fd5a27] transition-all duration-300">
                  <h3 className="font-bold mb-2 text-base lg:text-lg">Lifetime Support</h3>
                  <p className="text-xs lg:text-sm text-white/80">24/7 customer support plus automated monitoring for renewals and maintenance deadlines</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 lg:py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">CLIENT SUCCESS STORIES</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Trusted by 10,000+ Businesses Nationwide
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              From solo entrepreneurs to e-commerce brands—real stories from businesses we've helped protect
            </p>
          </div>
          
          <div className="md:hidden">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={4000}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:block">
            <Slider
              className="home-card-slider"
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={3}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={4000}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                  }
                }
              ]}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="px-4">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 lg:py-20 bg-slate-800/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <p className="text-[#fd5a27] font-semibold mb-2 text-sm lg:text-base">COMMON QUESTIONS</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Trademark Registration FAQs
            </h2>
            <p className="text-sm sm:text-base text-gray-300 mt-4">
              Expert answers to your trademark questions—from filing to approval
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left font-semibold text-sm lg:text-base text-white hover:text-[#fd5a27]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 text-sm lg:text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section - Urgency Focused */}
      <section className="py-12 lg:py-20 relative overflow-hidden text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200)'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-[#fd5a27]/90 to-slate-900/95"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-red-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-red-500/30 mb-6">
            <span className="text-sm font-medium text-red-300">⚠️ URGENT: Competitors filing trademarks daily</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            Protect Your Brand Before It's Too Late
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 lg:mb-8">
            Don't risk brand theft, Amazon suspensions, or costly legal battles. Federal trademark protection starts at just $45—filed within 24 hours with attorney review included.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-[#e04a1f] hover:to-[#d0421a] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-xl transition-all duration-300 w-full sm:w-auto"
            >
              Get Free Consultation Now
            </Button>
          </div>
          <p className="text-xs sm:text-sm text-gray-300 mt-6">
            ✓ No obligation | ✓ Money-back guarantee | ✓ 24-hour filing | ✓ 98% approval rate
          </p>
        </div>
      </section>

      <RegistrationModal isOpen={isModalOpen} onClose={handleCloseModal} selectedPackage={selectedPackage} />
    </div>
  );
};

export default HomePage;